import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnDestroy, OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { SearchService, SearchResult, SearchResults } from '../../shared/search.service';
import { BaseComponent } from '../general/base/base.component';
import { VivoService } from '../../vivo/vivo.service';
import { Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { InfoActionService } from '../../element-utils/element-actions/info-action/info-action.service';
import { MapService } from '../../shared/map/map.service';
import { MapPlace } from '../../shared/map/types';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../notification/notification.service';
import { SidebarService } from '../../shared/sidebar/sidebar.service';
import { NotificationEntry } from '../notification/notification.model';
import { AssetSearchResponse, Asset as VivoAsset } from '~app/vivo/vivo.model';
import { MapElement } from '~app/shared/map/map-element.model';
import { Point } from 'geojson';
import { compareLayerSearchResults } from '~app/shared/map/layer/utils/map.utils';
import { FocusActionService } from '~app/element-utils/element-actions/focus-action/focus-action.service';
import { groupBy } from 'lodash-es';

type AnyResult = SearchResult | MapPlace | NotificationEntry | VivoAsset;

/**
 * This component is used to display search field and search results in map toolbar
 */
@Component({
  selector: 'qm-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchComponent extends BaseComponent implements OnInit, OnDestroy {

  /**
   * Map of search result lists, Map is used so that results can be grouped in future.
   */
  public results: Map<string, SearchResults> = new Map();
  // additional results for each layer, grouped by aux source ID
  // they're shown in the results in the same groupings as the normal results
  public auxResults: Map<string, Map<string, SearchResults>> = new Map();
  public selectedFeature: AnyResult = null;
  public addressResults: MapPlace[] = [];
  public notificationResults: NotificationEntry[] = [];
  public query: string = null;
  public vivoResults: VivoAsset[] = [];

  // only scroll into view after keyboard navigation; messes with wheel based scrolling otherwise
  public scrollToResult: boolean = false;

  // used for scrolling active result into view
  @ViewChild('searchResults')
  public searchResults: ElementRef<HTMLDivElement>;

  @Input()
  public width = '24em';
  @Input()
  public maxHeight = '60vh';
  @Input()
  public targetPanel: string;
  @Input()
  public searchTooltipAdditionalText: TemplateRef<any>;
  @ViewChild('searchField', { static: true })
  public searchField: ElementRef<HTMLInputElement>;
  @ViewChild('content', { static: true })
  public content;

  public notLoadedLayerNames$ = this.mapService.notLoadedLayers$.pipe(
    map(notLoaded => {
      return [...notLoaded].map(layer => layer.name);
    })
  );

  /**
   * Subscription manager
   * @type {Subject<any>}
   */
  private readonly ngUnsubscribe: Subject<boolean> = new Subject();

  /**
   * @param {ChangeDetectorRef} changeDetector
   * @param {NgbModal} modalService
   * @param {ElementRef} elementRef
   * @param {SearchService} searchService
   * @param {MapService} mapService
   */
  constructor(private readonly changeDetector: ChangeDetectorRef,
              private readonly searchService: SearchService,
              private readonly mapService: MapService,
              private readonly translateService: TranslateService,
              private readonly infoActionService: InfoActionService,
              private readonly focusActionService: FocusActionService,
              private readonly vivoService: VivoService,
              private readonly notificationService: NotificationService,
              private readonly sidebarService: SidebarService) {
    super();
    this.name = 'SearchComponent';
  }

  /**
   * Angular lifecycle hook, subscribe to search results when component is created
   * @implements {OnInit}
   */
  ngOnInit(): void {
    this.searchService.searchResultChanged$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(data => {
      if (data.results?.length > 0) {
        if (data.auxiliary) {
          this.auxResults.set(data.sourceId, new Map(
            Object.values(groupBy(data.results, 'sourceId'))
              .map(group => [group[0].sourceId, {
                sourceId: group[0].sourceId,
                sourceName: group[0].sourceName,
                auxiliary: true,
                results: group,
              }])
          ));
        } else {
          this.results.set(data.sourceId, data);
        }
      } else {
        if (data.auxiliary) {
          this.auxResults.delete(data.sourceId);
        } else {
          this.results.delete(data.sourceId);
        }
      }
      this.scrollToResult = true;
      this.shiftActiveResult(0);
      this.changeDetector.markForCheck();
    });
  }

  /** @implements {OnDestroy} */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.query = null;
  }

  // scroll result list after changes to ensure selected result remains visible
  ngAfterViewChecked() {
    if (!this.scrollToResult || !this.searchResults?.nativeElement) {
      return;
    }

    const firstResult: Element = this.searchResults.nativeElement.querySelector('li');
    const activeResult: Element = this.searchResults.nativeElement.querySelector('li.active');
    if (!firstResult || !activeResult) {
      return;
    }

    this.scrollToResult = false;
    if (activeResult === firstResult) {
      // if the first item is selected, scroll to top to ensure the header is visible
      this.searchResults.nativeElement.scrollTo({ top: 0 });
    } else {
      // otherwise, just ensure the selected item is visible
      activeResult.scrollIntoView({ block: 'nearest' });
    }
  }

  @HostListener('document:keydown', ['$event'])
  public onGlobalKeydown(event: KeyboardEvent): void {
    if (event.key === 'f' && (event.metaKey === true || event.ctrlKey === true) && event.shiftKey === false) {
      this.activateSearchField();
      event.preventDefault();
    }
  }

  public onFocusedKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Down':
      case 'ArrowDown':
        this.shiftActiveResult(1);
        break;
      case 'Up':
      case 'ArrowUp':
        this.shiftActiveResult(-1);
        break;
      case 'Enter':
        this.commitSelection();
        break;
      case 'Esc':
      case 'Escape':
        this.onClickedOutside();
        break;
      default:
        // return early to avoid call to preventDefault
        return;
    }

    this.scrollToResult = true;
    event.preventDefault();
  }

  public onClickedOutside(): void {
    this.searchField.nativeElement.blur();
    this.query = null;
    this.selectedFeature = null;
    this.results.clear();
    this.auxResults.clear();
    this.vivoResults = [];
    this.addressResults = [];
    this.notificationResults = [];
    this.changeDetector.detectChanges();
  }

  public activateSearchField(): void {
    this.searchField.nativeElement.focus();
  }

  /**
   * Execute search
   */
  public search(): void {
    this.selectedFeature = null;
    // Emit query also, when it's empty
    this.searchService.search(this.query || '');
    if (this.query && this.query.length >= 1) {
      this.addressSearch(this.query);
      this.vivoSearch(this.query);
      this.notificationSearch(this.query);
    } else {
      this.results.clear();
      this.auxResults.clear();
      this.selectedFeature = null;
      this.changeDetector.markForCheck();
    }
  }

  private async vivoSearch(query: string): Promise<void> {
    this.vivoResults = [];
    let results: AssetSearchResponse;
    try {
      results = await this.vivoService.vivoSearch(query);
    } catch (e) {
      console.warn(e);
      return;
    }
    if (results?.docs?.length > 0) {
      this.vivoResults = results.docs;
      this.changeDetector.markForCheck();
    }
  }

  private async zoom(): Promise<void> {
    // in case it changes during the await
    const addressResult = this.selectedFeature as MapPlace;
    await this.mapService.showPlaceOnMap(addressResult);
  }

  /**
   * @returns the keys of all available map result groups, in priority order
   */
  public getMapResultKeys(): string[] {
    const keys: Set<string> = new Set([
      ...this.results.keys(),
      ...Array.from(this.auxResults.values()).flatMap(aux => Array.from(aux.keys())),
    ]);
    return Array.from(keys)
      .filter(key => key.startsWith('map'))
      .sort((a, b) => {
        const resulta = this.getResults(a).results[0];
        const resultb = this.getResults(b).results[0];
        return compareLayerSearchResults(resulta, resultb);
      });
  }

  /**
   * @returns map results for a given source, with auxiliary results folded in
   */
  public getResults(sourceId: string): SearchResults {
    const results: SearchResults = {
      sourceId,
      sourceName: this.results.get(sourceId)?.sourceName,
      results: (this.results.get(sourceId)?.results || []).slice(),
    };
    for (const aux of this.auxResults.values()) {
      const auxResults = aux.get(sourceId);
      if (auxResults) {
        // name might be missing if there are only auxiliary results
        if (!results.sourceName) {
          results.sourceName = auxResults.sourceName;
        }
        results.results.push(...auxResults.results);
      }
    }
    return results;
  }

  public async addressSearch(query: string): Promise<void> {
    this.addressResults = [];
    if (query && query.length > 0) {
      const results = await this.mapService.searchResults(query, this.translateService.currentLang);
      if (results) {
        this.addressResults = results;
        this.changeDetector.markForCheck();
      }
    }
  }

  public async notificationSearch(query: string): Promise<void> {
    this.notificationResults = [];
    if (query && query.length > 2) {
      const results = await this.notificationService.searchResults(query);
      if (results) {
        this.notificationResults = results;
        this.changeDetector.markForCheck();
      }
    }
  }

  public isThereResults(): boolean {
    return (this.results.size + this.auxResults.size + this.vivoResults.length + this.addressResults.length + this.notificationResults.length) > 0;
  }

  /**
   * Returns a flat list of all search results,
   * mimicking the ordering used by the template.
   */
  private getFlatResults(): AnyResult[] {
    const mapResults: AnyResult[] = this.getMapResultKeys().flatMap(key => this.getResults(key).results.slice(0, 10));
    return mapResults
      .concat(this.vivoResults)
      .concat(this.notificationResults)
      .concat(this.addressResults);
  }

  /**
   * Change the active result by the given number of places.
   * If no result is currently active, selects the first result.
   */
  private shiftActiveResult(offset: number): void {
    const results = this.getFlatResults();
    if (results.length === 0) {
      return;
    }

    const currentId = this.getResultId(this.selectedFeature);
    const index = results.findIndex(result => {
      if (!result || !this.selectedFeature) {
        return false;
      }
      if ('sourceId' in result && 'sourceId' in this.selectedFeature && result.sourceId !== this.selectedFeature.sourceId) {
        return false;
      }
      return this.getResultId(result) === currentId;
    });

    if (index === -1) {
      // default to first result if selected item has disappeared
      this.selectResult(results[0]);
    } else if (index + offset < 0) {
      // wrap around when going off the top of the list
      // JS modulo operator ignores but retains the sign, so flip negative offset manually after wrapping
      this.selectResult(results[((index + offset) % results.length) + results.length]);
    } else {
      // wrap around when going off the bottom of the list
      this.selectResult(results[((index + offset) % results.length)]);
    }
  }

  public selectResult(result: AnyResult): void {
    this.selectedFeature = result;
    this.changeDetector.markForCheck();
  }

  public async selectResultAndCommit(result: AnyResult): Promise<void> {
    this.selectResult(result);
    await this.commitSelection();
  }

  public getResultId(result: AnyResult): string {
    if (!result) {
      return 'null';
    }
    // MapElement
    if ('feature' in result) {
      if (result.feature.layer?.metadata?.uniqueBy) {
        return result.feature.properties[result.feature.layer.metadata.uniqueBy];
      }
      return result.feature.id;
    }
    // VivoAsset
    if ('_id' in result) {
      return result._id;
    }
    // NotificationEntry
    if ('id' in result) {
      return result.id.toString();
    }
    // MapPlace
    if ('description' in result) {
      return result.description;
    }
    // all valid results are handled by now...
    console.error('invalid search result', result);
    try {
      return JSON.stringify(result);
    } catch (err) {
      return '' + result;
    }
  }

  /**
   * Trigger the primary action upon click/enter, either
   * opening the info window or focusing the map.
   */
  private async commitSelection(): Promise<void> {
    if ('feature' in this.selectedFeature) {
      const result = this.selectedFeature as SearchResult;
      if (result.feature.layer?.metadata?.component) {
        this.mapService.changeCurrentFeature(this.selectedFeature.feature);
        this.infoActionService.openDetails(this.selectedFeature.feature.layer, this.selectedFeature.feature);
      } else {
        this.focusActionService.setFocus(result.feature as MapElement<Point>);
      }
    } else if ('_id' in this.selectedFeature) {
      window.open(`https://miprovivo.trackinno.com/assets/${this.selectedFeature._id}`, '_blank');
    } else if ('center' in this.selectedFeature) {
      const result = this.selectedFeature as SearchResult;
      const layer = this.mapService.getLayerList().find(layer => `map_${layer.id}` === result.sourceId);
      if (layer) {
        this.focusActionService.setFocus({
          id: result.id,
          properties: {
            id: result.id,
          },
          type: 'geojson',
          geometry: {
            type: 'Point',
            coordinates: result.center,
          },
          layer,
        });
      } else {
        this.mapService.flyTo({
          lat: result.center[1],
          lng: result.center[0],
        });
      }
    } else if ('id' in this.selectedFeature) {
      this.notificationService.setActive(+this.selectedFeature.id);
      this.sidebarService.requestSidebarPanel('Notification');
    } else {
      await this.zoom();
    }
    this.onClickedOutside();
    this.changeDetector.markForCheck();
  }
}
